import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  GetAggregateScoresRefreshInfoDocument,
  UiAction,
  UiPage,
  useGetAggregateScoresRefreshInfoQuery,
  useGetIntegrationsSummaryQuery,
  useGetWorkspaceSelectionsQuery,
  useRefreshAggregateScoresMutation,
  useRegisterUserActionMutation,
} from "~/operations";
import { FormProvider, useForm } from "react-hook-form";
import { CreateWorkspaceFormInput } from "~/pages/space/workspaces/components/CreateWorkspace/CreateWorkspacePage";
import { useEffect, useState } from "react";
import { mapQueryBuilderAPIToFormValues } from "~/pages/space/workspaces/utils";
import { Flex } from "~/components/Flex";
import { WorkspaceConditionBuilder } from "~/pages/space/workspaces/components";

import { HomeDashboardHeader } from "~/pages/space/home-dashboard/components/HomeDashboardHeader";
import { HomeDashboardInventoryOverviewCardSunburst } from "./components/HomeDashboardInventoryOverviewCard";

import { Space } from "~/lib/types";
import { ScopeType, SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { Link } from "react-router-dom";
import { ArrowDownward as ArrowDownwardIcon } from "@mui/icons-material";
import { LoadingPage } from "~/components/loading";
import { useInventory } from "~/pages/inventory/hooks";
import { useTopActionsCard } from "~/pages/space/home-dashboard/TopActionsCard/hooks/useTopActionsCard";
import { useTopActionsCardWithInitiatives } from "~/pages/space/home-dashboard/TopActionsCard/hooks/useTopActionsCardWithInitiatives";
import { useSpaceOrWorkspaceRisk } from "./SpaceOrWorkspaceRiskCard/hooks/useSpaceOrWorkspaceRisk";
import { useCanCreate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create";
import { useCriticalExposuresCard } from "~/pages/space/home-dashboard/critical-exposures/hooks/useCriticalExposuresCard";
import { useLoadScope } from "~/pages/space/home-dashboard/hooks/useLoadScope";
import { CanUpdateWorkspace } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/UpdateWorkspace";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { PriorityFindingsCard } from "~/pages/space/home-dashboard/PriorityFindingsCard/PriorityFindingsCard";
import { CriticalExposuresCard } from "~/pages/space/home-dashboard/critical-exposures/CriticalExposuresCard";
import { TopActionsCard } from "~/pages/space/home-dashboard/TopActionsCard/TopActionsCard";
import { TopActionsCardWithInitiatives } from "~/pages/space/home-dashboard/TopActionsCard/TopActionsCardWithInitiatives";
import { TopActionsProgressCard } from "~/pages/space/home-dashboard/TopActionsProgressCard/TopActionsProgressCard";
import { SprintStreakCard } from "~/pages/space/home-dashboard/SprintStreakCard/SprintStreakCard";
import { HomeDashboardVulnerabilitiesDonutChart } from "~/pages/space/home-dashboard/components/donut-charts/HomeDashboardVulnerabilitiesDonutChart";
import { HomeDashboardAdvisoriesDonutChart } from "~/pages/space/home-dashboard/components/donut-charts/HomeDashboardAdvisoriesDonutChart";
import { HomeDashboardCVEsDonutChart } from "~/pages/space/home-dashboard/components/donut-charts/HomeDashboardCVEsDonutChart";
import { MTTRSlaCard } from "~/pages/space/home-dashboard/MTTRSla/MTTRSlaCard";
import { CriticalExposuresCardV2 } from "~/pages/space/home-dashboard/critical-exposures/CriticalExposuresCardV2";
import SpaceRiskCard from "~/pages/space/home-dashboard/SpaceOrWorkspaceRiskCard/SpaceOrWorkspaceRiskCard";
import { SelectionAssetsCount } from "~/pages/space/workspaces/components/SelectionAssetsCount";
import { WorkspaceEmpty } from "~/pages/space/workspaces/components/WorkspaceEmpty";

export type HomeDashboardProps = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
};

export function HomeDashboard({ space, scope }: HomeDashboardProps) {
  const isLandscape = useMediaQuery("(orientation: landscape)");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { isFeatureEnabled } = useFeatureFlags();
  const [registerUserAction] = useRegisterUserActionMutation();

  // Send user action mutation on mount
  useEffect(() => {
    registerUserAction({
      variables: {
        input: {
          action: UiAction.View,
          page: UiPage.SpaceDashboard,
          scopeMrn: scope.mrn,
        },
      },
    }).then();
  }, []);
  const {
    assetsTotalCount,
    workspaceAssetsTotalCount,
    spaceAssetsTotalCount,
    loading: inventoryLoading,
    refetch,
  } = useInventory({
    scope,
  });
  const isMTNInitiativesEnabled = isFeatureEnabled("MTN Initiatives");
  const { refetch: refetchTopActionsAggregateScores } =
    useTopActionsCard(scope);
  const { refetch: refetchTopActionsInitiatives } =
    useTopActionsCardWithInitiatives(scope);
  const refetchTopActions = isMTNInitiativesEnabled
    ? refetchTopActionsInitiatives
    : refetchTopActionsAggregateScores;

  const isSpaceDashboard = scope.type === ScopeType.Space;
  const isWorkspaceDashboard = scope.type === ScopeType.Workspace;

  const { data: workspaceSelectionsData } = useGetWorkspaceSelectionsQuery({
    variables: {
      mrn: scope.mrn,
    },
    skip: !isWorkspaceDashboard,
  });

  const workspaceSelections =
    workspaceSelectionsData?.workspace?.__typename === "Workspace"
      ? workspaceSelectionsData?.workspace?.selections?.selections
      : [];

  const formMethods = useForm<CreateWorkspaceFormInput>({
    mode: "onBlur",
    defaultValues: {
      name: "",
      description: "",
      selections: [],
    },
  });

  const { refetch: refetchSpaceOrWorkspaceRisk } = useSpaceOrWorkspaceRisk(
    scope?.mrn,
  );

  useEffect(() => {
    if (!isWorkspaceDashboard) return;

    formMethods.reset({
      selections: mapQueryBuilderAPIToFormValues(workspaceSelections),
    });
  }, [workspaceSelections]);

  const { data: integrationSummaryData, loading: integrationSummaryLoading } =
    useGetIntegrationsSummaryQuery({
      variables: { input: { spaceMrn: space.mrn || "" } },
    });

  const hasIntegrations = Boolean(
    integrationSummaryData?.integrationsSummary?.total || 0 > 0,
  );
  const canCreateIntegration = useCanCreate(space);
  const isMoveTheNeedleFeatureEnabled = isFeatureEnabled("Move the needle");
  const isSprintStreakFeatureEnabled = isFeatureEnabled("Sprint Streak");

  const { refetch: refetchCriticalExposures } = useCriticalExposuresCard(
    scope?.mrn,
  );

  const {
    loading: isLoadingScope,
    findingsCount,
    assetCount,
    refetch: refetchSpace,
  } = useLoadScope({ skip: !isMoveTheNeedleFeatureEnabled });

  const [refreshAggregateScores] = useRefreshAggregateScoresMutation({
    refetchQueries: [GetAggregateScoresRefreshInfoDocument],
  });

  const {
    data: refreshInfoData,
    refetch: refetchRefreshInfo,
    startPolling,
    stopPolling,
  } = useGetAggregateScoresRefreshInfoQuery({
    variables: {
      groupMrn: space?.mrn || "",
    },
    skip: !isSpaceDashboard || !space?.mrn || !isMoveTheNeedleFeatureEnabled,
  });

  // After the refresh button is clicked, we start polling to check if the refresh is done
  // Once the refresh is done, we stop polling
  useEffect(() => {
    if (refreshInfoData?.aggregateScoresRefreshInfo.refreshInProgress) {
      startPolling(10000);
    } else {
      stopPolling();
    }
  }, [refreshInfoData?.aggregateScoresRefreshInfo.refreshInProgress]);

  const lastUpdatedTime =
    refreshInfoData?.aggregateScoresRefreshInfo.lastUpdatedAt || "";

  const onRefreshValues = () => {
    setIsRefreshing(true);

    Promise.all([
      refreshAggregateScores({
        variables: { groupMrn: space?.mrn || "" },
      }),
      refetchSpaceOrWorkspaceRisk(),
      refetchSpace(),
      refetchCriticalExposures(),
      refetchTopActions(),
    ]).then(async () => {
      await refetchRefreshInfo();
      setIsRefreshing(false);
    });
  };

  return (
    <Box>
      {isWorkspaceDashboard ? (
        <HomeDashboardHeader
          context={scope.type}
          title={scope.name}
          description={scope.description}
          additionalContent={
            <FormProvider {...formMethods}>
              <Grid size={12}>
                <Flex
                  sx={{ color: "text.primary", my: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h5" fontWeight={700}>
                    Workspace Asset Selections
                  </Typography>
                  <Flex alignItems="center" gap={2}>
                    <SelectionAssetsCount
                      selectionCount={workspaceAssetsTotalCount}
                      totalCount={spaceAssetsTotalCount}
                    />
                    <CanUpdateWorkspace scope={space}>
                      <Button
                        component={Link}
                        color="primary"
                        endIcon={
                          <ArrowDownwardIcon sx={{ rotate: "-90deg" }} />
                        }
                        to={`/space/settings?${scope.params}`}
                      >
                        Edit
                      </Button>
                    </CanUpdateWorkspace>
                  </Flex>
                </Flex>
                <WorkspaceConditionBuilder isReadonly />
              </Grid>
            </FormProvider>
          }
        />
      ) : (
        <HomeDashboardHeader
          context={scope.type}
          title={scope.name}
          description={scope.description}
          onRefresh={onRefreshValues}
          lastRefreshed={lastUpdatedTime}
          isRefreshing={isRefreshing}
        />
      )}

      {!inventoryLoading &&
        !integrationSummaryLoading &&
        assetsTotalCount > 0 &&
        !hasIntegrations &&
        canCreateIntegration && (
          <Alert
            severity="info"
            data-testid="no-integrations-banner"
            sx={{
              mb: 2,
              mt: -1,
              backgroundColor: "#EBF1FF",
              color: "#3B5BDB",
              "& .MuiAlert-action": {
                pt: 0,
              },
              "& .MuiAlert-message": {
                color: "#3B5BDB",
              },
              "& .MuiAlertTitle-root": {
                color: "#3B5BDB",
              },
              "& .MuiAlert-icon": {
                color: "#3B5BDB",
              },
            }}
            action={
              <Button
                component={Link}
                to="/space/integrations"
                sx={{
                  color: "#3B5BDB",
                  "&:hover": {
                    backgroundColor: "rgba(59, 91, 219, 0.04)",
                  },
                }}
              >
                Add integration
              </Button>
            }
          >
            <AlertTitle mb="0">
              Add your first integration to get started with Mondoo, and unlock
              powerful scanning and inventory capabilities!
            </AlertTitle>
          </Alert>
        )}

      {inventoryLoading && <LoadingPage what={`${scope.type} data`} />}

      {!inventoryLoading && (
        <Box>
          {assetsTotalCount === 0 && isWorkspaceDashboard && (
            <WorkspaceEmpty
              space={space}
              scope={scope}
              onRefreshComplete={() => {
                refetch();
              }}
            />
          )}
          {assetsTotalCount > 0 && (
            <Grid container spacing={3}>
              <Grid container spacing={3} size={12}>
                {isMoveTheNeedleFeatureEnabled ? (
                  <Grid size={{ xs: 12, sm: isLandscape ? 4 : 12, lg: 4 }}>
                    <SpaceRiskCard
                      isLoadingScope={isLoadingScope}
                      findingsCount={findingsCount}
                      assetCount={assetCount}
                      isSpaceDashboard={isSpaceDashboard}
                      scopeMrn={scope?.mrn}
                    />
                  </Grid>
                ) : (
                  <Grid size={{ xs: 12, sm: isLandscape ? 6 : 12, lg: 6 }}>
                    <HomeDashboardInventoryOverviewCardSunburst
                      scope={scope}
                      spaceScope={space}
                    />
                  </Grid>
                )}

                <Grid
                  container
                  size={{
                    xs: 12,
                    sm: isLandscape
                      ? isMoveTheNeedleFeatureEnabled
                        ? 8
                        : 6
                      : 12,
                    lg: isMoveTheNeedleFeatureEnabled ? 8 : 6,
                  }}
                >
                  <Grid size={12} height="auto">
                    {isMoveTheNeedleFeatureEnabled && (
                      <CriticalExposuresCardV2 scope={scope} />
                    )}
                    {!isMoveTheNeedleFeatureEnabled && (
                      <CriticalExposuresCard scope={scope} />
                    )}
                  </Grid>

                  <Grid
                    size={12}
                    height={isMoveTheNeedleFeatureEnabled ? "auto" : 560}
                  >
                    {!isMoveTheNeedleFeatureEnabled && (
                      <PriorityFindingsCard
                        scope={scope}
                        spaceScope={space}
                        assetsTotalCount={assetsTotalCount}
                      />
                    )}

                    {isMoveTheNeedleFeatureEnabled &&
                      (isMTNInitiativesEnabled ? (
                        <TopActionsCardWithInitiatives
                          scope={scope}
                          spaceScope={space}
                          lastRefreshed={lastUpdatedTime}
                          isRefreshing={isRefreshing}
                        />
                      ) : (
                        <TopActionsCard
                          scope={scope}
                          spaceScope={space}
                          lastRefreshed={lastUpdatedTime}
                          isRefreshing={isRefreshing}
                        />
                      ))}
                  </Grid>
                </Grid>
              </Grid>
              {isMoveTheNeedleFeatureEnabled && (
                <Grid container size={12}>
                  <TopActionsProgressCard scope={scope} spaceScope={space} />
                </Grid>
              )}
              {isSprintStreakFeatureEnabled && (
                <Grid container size={12}>
                  <SprintStreakCard scope={scope} />
                </Grid>
              )}
              <Grid container size={12}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <HomeDashboardVulnerabilitiesDonutChart scope={scope} />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <HomeDashboardAdvisoriesDonutChart scope={scope} />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <HomeDashboardCVEsDonutChart scope={scope} />
                </Grid>
                <Grid size={12}>
                  <MTTRSlaCard />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
}
